import {
  Box,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useProduct } from "src/context/ProductProvider";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useCallback, useMemo } from "react";
import { Check } from "@mui/icons-material";
import { theme } from "src/assets/theme";
import { handleFocusIntoView } from "src/utils/focusIntoView";
import HSAFSADisclaimer from "src/pages/Onboarding/Payment/components/HSAFSADisclaimer";
import { medicationInfoMap } from "src/pages/Onboarding/Payment/utils/mapMedicationInfoToProduct";
import WhiteCard from "src/shared/components/WhiteCard";
import { MedicationIcon } from "src/shared/components/MedicationIcon";
import PaymentProcessor from "src/pages/Onboarding/Payment/components/PaymentProcessor";
import { useQuantity } from "src/hooks/useQuantity";
import { useSelectedProduct } from "src/hooks/useSelectedProduct";
import { useGetPatientCouponsQuery } from "src/api/services/patientCouponsApi";
import CouponView from "src/pages/Onboarding/Payment/components/CouponView";

const OndansetronDisplay = () => {
  const isMobile = useIsMobile();
  const [selectedProduct] = useSelectedProduct();
  const [quantity, setQuantity] = useQuantity(10);
  const page = "payment-information";
  const { t } = useTranslation();
  const productSlug = useProduct((p) => p.slug);
  const category = useProduct((p) => p.category.name);
  const { data: coupons = [] } = useGetPatientCouponsQuery({
    product_slug: productSlug,
    redeemed: false,
  });

  const informationArray = useMemo(
    () => [
      `payment-information.dashboard.${productSlug}.list.0`,
      `payment-information.dashboard.${productSlug}.list.1`,
      `payment-information.dashboard.${productSlug}.list.2`,
      `payment-information.dashboard.${productSlug}.list.3`,
    ],
    [productSlug],
  );

  const medicationInfo = useMemo(
    () => medicationInfoMap[productSlug],
    [productSlug],
  );

  const handleSelectQuantity = useCallback(
    (q: number) => {
      setQuantity(q);
    },
    [setQuantity],
  );

  const defaultProductPrice = useMemo(() => {
    if (selectedProduct == null) {
      return "0";
    }

    const discount = coupons.reduce((acc, c) => (acc += c.amount), 0);

    const total = selectedProduct.amount * quantity - discount;
    const hasFraction = total % 1 !== 0;

    return total.toLocaleString("en-US", {
      minimumFractionDigits: hasFraction ? 2 : 0,
    });
  }, [coupons, quantity, selectedProduct]);

  const subHeader = useMemo(() => {
    return t(`${page}.${productSlug}.subheader`);
  }, [productSlug, t]);

  return (
    <>
      <Stack sx={{ position: "relative" }}>
        <HSAFSADisclaimer category={category} />
        <WhiteCard
          onClick={() => handleFocusIntoView("payment-input")}
          sx={{
            mt: "-15px", // pulls it up by the same radius
            borderRadius: "0 0 10px 10px",
            px: 2,
            paddingBottom: isMobile ? "20px" : "0px",
            zIndex: 1,
            cursor: "pointer",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ position: "relative", bottom: isMobile ? 0 : 8 }}
          >
            <Stack gap={1}>
              <Typography variant={isMobile ? "body1" : "h5"} fontWeight={600}>
                {t(medicationInfo.header)}
              </Typography>
              {medicationInfo.text != null ? (
                <Typography variant={isMobile ? "body2" : "h6"}>
                  {t(medicationInfo.text)}
                </Typography>
              ) : null}
            </Stack>
            <MedicationIcon
              slug={productSlug}
              name={medicationInfo.name}
              width={isMobile ? 130 : 180}
              height={isMobile ? 130 : 180}
            />
          </Stack>
        </WhiteCard>
      </Stack>
      <Stack gap={2} onClick={() => handleFocusIntoView("payment-input")}>
        <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
          {subHeader}
        </Typography>
        {informationArray.map((i) => (
          <Box key={i} display="flex" gap={1} alignItems="center">
            <Check
              sx={{ color: theme.palette.primary.main }}
              fontSize="small"
            />
            <Typography variant={isMobile ? "body2" : "body1"}>
              {t(i)}
            </Typography>
          </Box>
        ))}
      </Stack>
      <Stack gap={1}>
        <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
          {t(`${page}.dashboard.select-label`, "Quantity")}
        </Typography>
        <FormControl>
          <Select
            value={quantity}
            onChange={(e) => handleSelectQuantity(Number(e.target.value))}
          >
            {[10, 15, 20].map((q, idx) => (
              <MenuItem key={idx} value={q}>
                {q}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Stack gap={3}>
        <Stack gap={4}>
          <Stack gap={2}>
            {coupons.map((c) => (
              <CouponView coupon={c} />
            ))}
            <Stack gap={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize={"22px"} fontWeight={600}>
                  {t(`${page}.total`)}
                </Typography>
                <Typography fontSize={"22px"} fontWeight={600}>
                  ${defaultProductPrice}
                </Typography>
              </Stack>
            </Stack>
            <Divider sx={{ position: "relative", top: 15 }} />
          </Stack>
          {selectedProduct ? (
            <PaymentProcessor
              product={selectedProduct}
              quantity={quantity}
              coupon={null}
              dueToday={Number(defaultProductPrice)}
            />
          ) : null}
        </Stack>
      </Stack>
    </>
  );
};

export default OndansetronDisplay;
